import { Filter, Plus, Save, X } from "lucide-react";
import { Button } from "../../tailwind/button";
import { Input } from "../../tailwind/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../tailwind/select";
import { cn } from "../../../lib/utils";
import { CONTACTS_PRIMARY_BUTTON_CLASS } from "../../../pages/whatsapp/data/contactsData";

const EMPTY_OPTIONS = {
  leadStatuses: [],
  tags: [],
};

function FilterValueSelect({
  placeholder,
  value,
  options,
  emptyMessage,
  onChange,
}) {
  if (!options.length) {
    return (
      <div className="w-full sm:flex-1 min-w-0 sm:min-w-[200px] h-9 px-3 flex items-center text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-200 dark:border-slate-700 rounded-md bg-gray-50 dark:bg-slate-950">
        {emptyMessage}
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:flex-1 bg-white dark:bg-slate-950 h-9 min-w-0 sm:min-w-[200px] border-gray-200 dark:border-slate-800">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ContactsFilters({
  filters,
  filteredCount,
  totalCount,
  currentPage = 1,
  loading = false,
  loaded = false,
  showSaveSegment,
  segmentName,
  segmentNameError = "",
  segmentSaving = false,
  hasActiveFilters,
  filterOptions = EMPTY_OPTIONS,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onClearFilters,
  onShowSaveSegment,
  onSegmentNameChange,
  onSaveSegment,
  onCancelSaveSegment,
}) {
  const isLoading = loading || !loaded;
  const showNoMatch = !isLoading && hasActiveFilters && filteredCount === 0;
  const showEmptyPage =
    !isLoading && !hasActiveFilters && filteredCount === 0 && totalCount === 0;

  return (
    <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            Advanced Filters
          </h3>
        </div>
        <button
          type="button"
          onClick={onClearFilters}
          className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          Clear all
        </button>
      </div>

      <div className="space-y-3">
        {filters.map((filter, index) => (
          <div
            key={`filter-${index}`}
            className="flex flex-col sm:flex-row sm:items-center gap-2"
          >
            <Select
              value={filter.field}
              onValueChange={(value) => onUpdateFilter(index, "field", value)}
            >
              <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-slate-950 h-9 border-gray-200 dark:border-slate-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lead_status">Lead Status</SelectItem>
                <SelectItem value="tags">Tags</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.operator}
              onValueChange={(value) => onUpdateFilter(index, "operator", value)}
            >
              <SelectTrigger className="w-full sm:w-[140px] bg-white dark:bg-slate-950 h-9 border-gray-200 dark:border-slate-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {filter.field === "lead_status" ? (
                  <>
                    <SelectItem value="is">is</SelectItem>
                    <SelectItem value="is not">is not</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="includes">includes</SelectItem>
                    <SelectItem value="does not include">does not include</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 w-full sm:flex-1 min-w-0">
              {filter.field === "lead_status" ? (
                <FilterValueSelect
                  placeholder="Select status..."
                  value={filter.value}
                  options={filterOptions.leadStatuses}
                  emptyMessage="No statuses on this page"
                  onChange={(value) => onUpdateFilter(index, "value", value)}
                />
              ) : (
                <FilterValueSelect
                  placeholder="Select tag..."
                  value={filter.value}
                  options={filterOptions.tags}
                  emptyMessage="No tags on this page"
                  onChange={(value) => onUpdateFilter(index, "value", value)}
                />
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0"
                onClick={() => onRemoveFilter(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        {filters.length < 5 && (
          <button
            type="button"
            onClick={onAddFilter}
            className="text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 flex items-center gap-1 mt-2"
          >
            <Plus className="h-3.5 w-3.5" /> Add filter
          </button>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div
            className={cn(
              "text-lg font-bold flex items-center gap-2",
              isLoading
                ? "text-gray-500 dark:text-gray-400"
                : showNoMatch
                  ? "text-red-500 dark:text-red-400"
                  : filteredCount > 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-gray-700 dark:text-gray-300"
            )}
          >
          </div>
          {showNoMatch && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-1">
              No contacts match. Try adjusting your filters.
            </p>
          )}
          {showEmptyPage && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              No contacts on this page yet.
            </p>
          )}
        </div>

                {hasActiveFilters && filteredCount > 0 && !isLoading && (
          <div className="w-full sm:w-auto">
            {!showSaveSegment ? (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                onClick={onShowSaveSegment}
              >
                <Save className="h-4 w-4 mr-2" />
                Save as Segment
              </Button>
            ) : (
              <div className="space-y-1 w-full">
                <div className="flex flex-row items-center gap-2 w-full">
                  <Input
                    placeholder="Enter segment name..."
                    value={segmentName}
                    onChange={(event) => onSegmentNameChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        onSaveSegment();
                      }
                    }}
                    maxLength={100}
                    aria-invalid={Boolean(segmentNameError)}
                    className={cn(
                      "h-9 min-w-0 flex-1 sm:w-[200px] sm:flex-none bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800",
                      segmentNameError
                        ? "border-red-500 dark:border-red-500"
                        : ""
                    )}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    className={cn(CONTACTS_PRIMARY_BUTTON_CLASS, "h-9 shrink-0")}
                    onClick={onSaveSegment}
                    disabled={segmentSaving}
                  >
                    {segmentSaving ? "Saving…" : "Save"}
                  </Button>
                  <button
                    type="button"
                    onClick={onCancelSaveSegment}
                    disabled={segmentSaving}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white shrink-0 whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
                {segmentNameError ? (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {segmentNameError}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
